import { useState, useCallback } from 'react'
import { useDataModelingAutoSave } from '@/hooks/useDataModelingAutoSave'
import { useInterviewStore, DataModelingEntity, DataModelingRelationship, DataModelingComment } from '@/store/interviewStore'
import { 
  Database, Table, Key, Link2, Plus, Trash2, Edit, 
  Save, X, RotateCcw, Download, ZoomIn, ZoomOut, 
  MessageSquare, Move, ChevronDown, ChevronRight
} from 'lucide-react'

interface Attribute {
  id: string
  name: string
  type: string
  isPrimary: boolean
  isForeign: boolean
  isRequired: boolean
  referencedTableId?: string
  referencedColumnId?: string
}

interface ForeignKeyRelationship {
  id: string
  fromEntityId: string
  fromAttributeId: string
  toEntityId: string
  toAttributeId: string
}

// Extended entity type to include attributes 
interface Entity extends DataModelingEntity {
  attributes: Attribute[]
}

// Extended relationship type to include label
interface Relationship extends DataModelingRelationship {
  label: string
}

// Extended comment type to include dimensions
interface Comment extends DataModelingComment {
  width: number
  height: number
}

export default function DataModeling() {
  const store = useInterviewStore()
  const dataModelingData = store.getDataModelingData()
  
  // Bridge variables to maintain compatibility with existing functions - convert store types to local types
  const entities: Entity[] = dataModelingData.entities.map(entity => ({
    ...entity,
    attributes: [] as Attribute[] // Default empty attributes - should be loaded separately
  }))
  const relationships: Relationship[] = dataModelingData.relationships.map(rel => ({
    ...rel,
    label: '' // Default empty label
  }))
  const fkRelationships: ForeignKeyRelationship[] = dataModelingData.fkRelationships.map(fk => ({
    id: fk.id,
    fromEntityId: fk.fromEntityId,
    fromAttributeId: fk.fromField,
    toEntityId: fk.toEntityId,
    toAttributeId: fk.toField
  }))
  const comments: Comment[] = dataModelingData.comments.map(comment => ({
    ...comment,
    width: 200, // Default width
    height: 100 // Default height
  }))
  const zoom = dataModelingData.zoom
  const panX = dataModelingData.panX
  const panY = dataModelingData.panY

  // Bridge functions for local state setters
  const setEntities = (newEntities: Entity[]) => {
    // Convert Entity[] to DataModelingEntity[] by excluding attributes
    const storeEntities: DataModelingEntity[] = newEntities.map(({ attributes, ...entity }) => entity)
    store.updateDataModelingEntities(storeEntities)
  }
  const setRelationships = (newRels: Relationship[]) => {
    // Convert Relationship[] to DataModelingRelationship[] by excluding label
    const storeRels: DataModelingRelationship[] = newRels.map(({ label, ...rel }) => rel)
    store.updateDataModelingRelationships(storeRels)
  }
  const setFkRelationships = (newFkRels: ForeignKeyRelationship[]) => {
    // Convert ForeignKeyRelationship[] to DataModelingRelationship[] by mapping fields
    const storeRels: DataModelingRelationship[] = newFkRels.map(fk => ({
      id: fk.id,
      fromEntityId: fk.fromEntityId,
      toEntityId: fk.toEntityId,
      type: 'one-to-many' as const,
      fromField: fk.fromAttributeId,
      toField: fk.toAttributeId
    }))
    store.updateDataModelingFKRelationships(storeRels)
  }
  const setComments = (newComments: Comment[]) => {
    // Convert Comment[] to DataModelingComment[] by excluding width/height
    const storeComments: DataModelingComment[] = newComments.map(({ width, height, ...comment }) => comment)
    store.updateDataModelingComments(storeComments)
  }
  const setZoom = (zoomValue: number | ((prev: number) => number)) => {
    const newZoom = typeof zoomValue === 'function' ? zoomValue(dataModelingData.zoom) : zoomValue
    store.updateDataModelingZoom(newZoom)
  }
  const setPanX = (x: number) => store.updateDataModelingPan(x, dataModelingData.panY)
  const setPanY = (y: number) => store.updateDataModelingPan(dataModelingData.panX, y)
  
  // Use local state only for UI interactions
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null)
  const [showEntityModal, setShowEntityModal] = useState<string | null>(null)
  const [showFkModal, setShowFkModal] = useState<{entityId: string, attributeId: string} | null>(null)
  const [draggedEntity, setDraggedEntity] = useState<string | null>(null)
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null)
  const [isPanning, setIsPanning] = useState(false)
  const [lastPanPoint, setLastPanPoint] = useState({x: 0, y: 0})
  const [isToolsCollapsed, setIsToolsCollapsed] = useState(false)

  const { isDirty, lastSaved } = useDataModelingAutoSave({ 
    intervalMs: 5000,
    content: dataModelingData.content,
    entities: dataModelingData.entities,
    relationships: dataModelingData.relationships,
    fkRelationships: dataModelingData.fkRelationships,
    comments: dataModelingData.comments,
    zoom: dataModelingData.zoom,
    panX: dataModelingData.panX,
    panY: dataModelingData.panY
  })

  const handleContentChange = (newContent: string) => {
    store.updateDataModelingContent(newContent)
  }

  // Zoom and Pan functions
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.25))
  }

  const handleZoomReset = () => {
    setZoom(1)
    setPanX(0)
    setPanY(0)
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      // Zoom on Ctrl+wheel
      const delta = -e.deltaY
      if (delta > 0) {
        handleZoomIn()
      } else {
        handleZoomOut()
      }
    } else {
      // Pan on regular wheel
      setPanX(dataModelingData.panX - e.deltaX)
      setPanY(dataModelingData.panY - e.deltaY)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      // Middle mouse button or Alt+Left mouse for panning
      setIsPanning(true)
      setLastPanPoint({x: e.clientX, y: e.clientY})
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x
      const deltaY = e.clientY - lastPanPoint.y
      setPanX(dataModelingData.panX + deltaX)
      setPanY(dataModelingData.panY + deltaY)
      setLastPanPoint({x: e.clientX, y: e.clientY})
    }
  }

  const handleMouseUp = () => {
    setIsPanning(false)
  }

  const addEntity = () => {
    const newEntity = {
      id: `entity-${Date.now()}`,
      name: 'New Entity',
      x: (Math.random() * 400 + 100),
      y: (Math.random() * 300 + 100),
      attributes: [],
      fields: [] // Required by store interface
    } as Entity
    setEntities([...entities, newEntity])
    setShowEntityModal(newEntity.id)
  }

  const addComment = () => {
    const newComment = {
      id: `comment-${Date.now()}`,
      text: 'New Comment',
      x: (Math.random() * 400 + 100),
      y: (Math.random() * 300 + 100),
      width: 200,
      height: 100,
      color: '#yellow' // Required by store interface
    } as Comment
    setComments([...comments, newComment])
  }

  const updateComment = (commentId: string, updates: Partial<Comment>) => {
    setComments(comments.map(comment => 
      comment.id === commentId ? { ...comment, ...updates } : comment
    ))
  }

  const deleteComment = (commentId: string) => {
    setComments(comments.filter(c => c.id !== commentId))
  }

  const updateEntity = (entityId: string, updates: Partial<Entity>) => {
    setEntities(entities.map(entity => 
      entity.id === entityId ? { ...entity, ...updates } : entity
    ))
  }

  const deleteEntity = (entityId: string) => {
    setEntities(entities.filter(e => e.id !== entityId))
    setRelationships(relationships.filter(r => 
      r.fromEntityId !== entityId && r.toEntityId !== entityId
    ))
    setFkRelationships(fkRelationships.filter(fk => 
      fk.fromEntityId !== entityId && fk.toEntityId !== entityId
    ))
  }

  const addAttribute = (entityId: string) => {
    const newAttribute: Attribute = {
      id: `attr-${Date.now()}`,
      name: 'new_attribute',
      type: 'VARCHAR(255)',
      isPrimary: false,
      isForeign: false,
      isRequired: false
    }
    
    updateEntity(entityId, {
      attributes: [...(entities.find(e => e.id === entityId)?.attributes || []), newAttribute]
    })
  }

  const updateAttribute = (entityId: string, attrId: string, updates: Partial<Attribute>) => {
    const entity = entities.find(e => e.id === entityId)
    if (entity) {
      updateEntity(entityId, {
        attributes: (entity.attributes || []).map(attr => 
          attr.id === attrId ? { ...attr, ...updates } : attr
        )
      })
    }
  }

  const deleteAttribute = (entityId: string, attrId: string) => {
    const entity = entities.find(e => e.id === entityId)
    if (entity) {
      updateEntity(entityId, {
        attributes: (entity.attributes || []).filter(attr => attr.id !== attrId)
      })
      // Also remove any FK relationships involving this attribute
      setFkRelationships(fkRelationships.filter(fk => 
        fk.fromAttributeId !== attrId && fk.toAttributeId !== attrId
      ))
    }
  }

  const createForeignKeyRelationship = (fromEntityId: string, fromAttributeId: string, toEntityId: string, toAttributeId: string) => {
    const newFkRelationship: ForeignKeyRelationship = {
      id: `fk-${Date.now()}`,
      fromEntityId,
      fromAttributeId,
      toEntityId,
      toAttributeId
    }
    setFkRelationships([...fkRelationships, newFkRelationship])
    
    // Update the attribute to mark it as foreign and store reference
    updateAttribute(fromEntityId, fromAttributeId, {
      isForeign: true,
      referencedTableId: toEntityId,
      referencedColumnId: toAttributeId
    })
  }

  const removeForeignKeyRelationship = (fkId: string) => {
    const fk = fkRelationships.find(f => f.id === fkId)
    if (fk) {
      setFkRelationships(fkRelationships.filter(f => f.id !== fkId))
      // Update the attribute to remove foreign key marking
      updateAttribute(fk.fromEntityId, fk.fromAttributeId, {
        isForeign: false,
        referencedTableId: undefined,
        referencedColumnId: undefined
      })
    }
  }

  const handleForeignKeyClick = (entityId: string, attributeId: string) => {
    const attribute = entities.find(e => e.id === entityId)?.attributes.find(a => a.id === attributeId)
    if (attribute?.isForeign) {
      // If already a foreign key, show option to remove
      const fk = fkRelationships.find(f => f.fromAttributeId === attributeId)
      if (fk && window.confirm('Remove foreign key relationship?')) {
        removeForeignKeyRelationship(fk.id)
      }
    } else {
      // Show modal to select target table and column
      setShowFkModal({ entityId, attributeId })
    }
  }

  const createRelationship = (fromId: string, toId: string) => {
    const newRelationship = {
      id: `rel-${Date.now()}`,
      fromEntityId: fromId,
      toEntityId: toId,
      type: 'one-to-many' as const,
      label: '',
      fromField: '',
      toField: ''
    } as Relationship
    setRelationships([...relationships, newRelationship])
  }

  const handleEntityClick = (entityId: string) => {
    if (connectingFrom) {
      if (connectingFrom !== entityId) {
        createRelationship(connectingFrom, entityId)
      }
      setConnectingFrom(null)
    } else {
      setSelectedEntity(entityId)
    }
  }

  const getSaveStatus = () => {
    if (isDirty) {
      return <span className="text-amber-600">● Unsaved changes</span>
    } else if (lastSaved) {
      return <span className="text-green-600">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
    }
    return <span className="text-gray-500">No changes</span>
  }

  const exportSchema = () => {
    const schema = {
      entities: entities.map(entity => ({
        name: entity.name,
        attributes: (entity.attributes || []).map(attr => ({
          name: attr.name,
          type: attr.type,
          constraints: {
            primary_key: attr.isPrimary,
            foreign_key: attr.isForeign,
            required: attr.isRequired
          }
        }))
      })),
      relationships: relationships.map(rel => ({
        from: entities.find(e => e.id === rel.fromEntityId)?.name,
        to: entities.find(e => e.id === rel.toEntityId)?.name,
        type: rel.type,
        label: rel.label
      })),
      timestamp: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(schema, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'data-model-schema.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Database className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold">Data Modeling</h2>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-sm">
              {getSaveStatus()}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={addEntity}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Entity</span>
              </button>
              
              <button
                onClick={() => {
                  setEntities([])
                  setRelationships([])
                  setFkRelationships([])
                  setComments([])
                  setSelectedEntity(null)
                  setConnectingFrom(null)
                }}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear</span>
              </button>
              
              <button
                onClick={addComment}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Add Comment</span>
              </button>
              
              <button
                onClick={exportSchema}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              
              <div className="flex items-center space-x-2 bg-gray-100 rounded px-2 py-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-600 min-w-[3rem] text-center">
                  {Math.round(dataModelingData.zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={handleZoomReset}
                  className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded text-xs"
                  title="Reset Zoom"
                >
                  <Move className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          Design your database schema with entities, attributes, and relationships. Click entities to edit, or use connect mode to create relationships.
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Tools */}
        <div className={`${isToolsCollapsed ? 'w-12' : 'w-1/4'} p-4 border-r border-gray-200 overflow-y-auto transition-all duration-300`}>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-semibold text-gray-900 ${isToolsCollapsed ? 'hidden' : ''}`}>Tools</h3>
                <button
                  onClick={() => setIsToolsCollapsed(!isToolsCollapsed)}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  title={isToolsCollapsed ? 'Expand Tools' : 'Collapse Tools'}
                >
                  {isToolsCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
              {!isToolsCollapsed && (
                <div className="space-y-2">
                  <button
                    onClick={() => setConnectingFrom(connectingFrom ? null : 'connect')}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                      connectingFrom ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Link2 className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {connectingFrom ? 'Cancel Connect' : 'Connect Entities'}
                    </span>
                  </button>
                </div>
              )}
              {isToolsCollapsed && (
                <div className="flex justify-center">
                  <button
                    onClick={() => setConnectingFrom(connectingFrom ? null : 'connect')}
                    className={`p-2 rounded-lg transition-colors ${
                      connectingFrom ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title={connectingFrom ? 'Cancel Connect' : 'Connect Entities'}
                  >
                    <Link2 className="w-5 h-5" />
                  </button>
                </div>
              )}
              {connectingFrom && !isToolsCollapsed && (
                <div className="mt-2 p-2 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                  Click on two entities to create a relationship
                </div>
              )}
            </div>

            {!isToolsCollapsed && (
              <>
                <div>
                  <h3 className="font-semibold mb-2 text-gray-900">Entity Relationships</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-0.5 bg-gray-400"></div>
                      <span>One to One</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-0.5 bg-gray-400"></div>
                      <div className="text-xs">→</div>
                      <span>One to Many</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-0.5 bg-gray-400"></div>
                      <div className="text-xs">↔</div>
                      <span>Many to Many</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2 text-gray-900">Instructions</h3>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Click "Add Entity" to create new entities</li>
                    <li>• Click entities to edit attributes</li>
                    <li>• Use "Connect Entities" to create relationships</li>
                    <li>• Click "Add Comment" to add annotations</li>
                    <li>• Drag entities and comments to reposition</li>
                    <li>• Use mouse wheel or zoom controls to zoom</li>
                    <li>• Hold middle mouse or Alt+drag to pan</li>
                    <li>• Export schema when complete</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 p-4">
          <div 
            className="h-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg relative overflow-hidden"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          >
            <div
              style={{
                transform: `translate(${dataModelingData.panX}px, ${dataModelingData.panY}px) scale(${dataModelingData.zoom})`,
                transformOrigin: '0 0',
                width: '100%',
                height: '100%',
                position: 'relative'
              }}
            >
            {dataModelingData.entities.length === 0 && dataModelingData.comments.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Database className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Click "Add Entity" to start designing your data model</p>
                </div>
              </div>
            )}

            {/* Render Comments */}
            {dataModelingData.comments.map((comment) => (
              <div
                key={comment.id}
                className="absolute bg-yellow-100 border-2 border-yellow-300 rounded-lg p-2 shadow-lg resize cursor-move"
                style={{
                  left: comment.x,
                  top: comment.y,
                  width: (comment as any).width || 200,
                  minWidth: '120px',
                  minHeight: '60px'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation()
                  const startX = e.clientX - comment.x
                  const startY = e.clientY - comment.y
                  
                  const handleMouseMove = (e: MouseEvent) => {
                    updateComment(comment.id, {
                      x: e.clientX - startX,
                      y: e.clientY - startY
                    })
                  }
                  
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove)
                    document.removeEventListener('mouseup', handleMouseUp)
                  }
                  
                  document.addEventListener('mousemove', handleMouseMove)
                  document.addEventListener('mouseup', handleMouseUp)
                }}
              >
                <div className="flex items-start justify-between mb-1">
                  <MessageSquare className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteComment(comment.id)
                    }}
                    className="p-0.5 text-yellow-600 hover:text-red-600 hover:bg-yellow-200 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <textarea
                  value={comment.text}
                  onChange={(e) => updateComment(comment.id, { text: e.target.value })}
                  className="w-full bg-transparent resize-none border-none outline-none text-xs text-yellow-800 placeholder-yellow-500"
                  placeholder="Add your comment..."
                  rows={2}
                  style={{ minHeight: '40px' }}
                />
              </div>
            ))}

            {/* Render Entities */}
            {dataModelingData.entities.map((entity) => (
              <div
                key={entity.id}
                onClick={() => handleEntityClick(entity.id)}
                onMouseDown={(e) => {
                  if (connectingFrom) return
                  setDraggedEntity(entity.id)
                  const startX = e.clientX - entity.x
                  const startY = e.clientY - entity.y
                  
                  const handleMouseMove = (e: MouseEvent) => {
                    updateEntity(entity.id, {
                      x: e.clientX - startX,
                      y: e.clientY - startY
                    })
                  }
                  
                  const handleMouseUp = () => {
                    setDraggedEntity(null)
                    document.removeEventListener('mousemove', handleMouseMove)
                    document.removeEventListener('mouseup', handleMouseUp)
                  }
                  
                  document.addEventListener('mousemove', handleMouseMove)
                  document.addEventListener('mouseup', handleMouseUp)
                }}
                className={`absolute bg-white border-2 rounded-lg shadow-lg cursor-pointer transition-all ${
                  selectedEntity === entity.id ? 'border-blue-500 shadow-xl' : 'border-gray-300 hover:border-gray-400'
                } ${connectingFrom ? 'hover:border-blue-400' : 'hover:shadow-xl'}`}
                style={{
                  left: entity.x,
                  top: entity.y,
                  minWidth: '200px',
                  maxWidth: '250px'
                }}
              >
                {/* Entity Header */}
                <div className="bg-blue-100 px-3 py-2 rounded-t-lg border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Table className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-800">{entity.name}</span>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowEntityModal(entity.id)
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-200 rounded"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteEntity(entity.id)
                        }}
                        className="p-1 text-red-600 hover:bg-red-200 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Attributes */}
                <div className="p-2">
                  {(!(entity as any).attributes || (entity as any).attributes.length === 0) ? (
                    <div className="text-xs text-gray-500 text-center py-2">
                      No attributes. Click edit to add.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(entity as any).attributes.slice(0, 5).map((attr: any) => (
                        <div key={attr.id} className="flex items-center justify-between text-xs">
                          <div className="flex items-center space-x-1">
                            {attr.isPrimary && <Key className="w-3 h-3 text-yellow-500" />}
                            {attr.isForeign && <Link2 className="w-3 h-3 text-blue-500" />}
                            <span className={attr.isPrimary ? 'font-semibold' : ''}>{attr.name}</span>
                          </div>
                          <span className="text-gray-500">{attr.type}</span>
                        </div>
                      ))}
                      {((entity as any).attributes || []).length > 5 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{((entity as any).attributes || []).length - 5} more...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Simple relationship lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {dataModelingData.relationships.map((rel) => {
                const fromEntity = dataModelingData.entities.find(e => e.id === rel.fromEntityId)
                const toEntity = dataModelingData.entities.find(e => e.id === rel.toEntityId)
                if (!fromEntity || !toEntity) return null

                const fromX = fromEntity.x + 100
                const fromY = fromEntity.y + 50
                const toX = toEntity.x + 100
                const toY = toEntity.y + 50

                return (
                  <line
                    key={rel.id}
                    x1={fromX}
                    y1={fromY}
                    x2={toX}
                    y2={toY}
                    stroke="#374151"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead)"
                  />
                )
              })}
              
              {/* Foreign Key relationship lines - L-shaped */}
              {dataModelingData.fkRelationships.map((fk) => {
                const fromEntity = dataModelingData.entities.find(e => e.id === fk.fromEntityId)
                const toEntity = dataModelingData.entities.find(e => e.id === fk.toEntityId)
                if (!fromEntity || !toEntity) return null

                // Calculate connection points
                const fromX = fromEntity.x + 200 // Right edge of source entity
                const fromY = fromEntity.y + 50  // Middle of source entity
                const toX = toEntity.x           // Left edge of target entity
                const toY = toEntity.y + 50      // Middle of target entity

                // Create L-shaped path: horizontal first, then vertical
                const midX = fromX + (toX - fromX) * 0.7 // 70% of the way horizontally

                return (
                  <g key={fk.id}>
                    {/* Horizontal line from source */}
                    <line
                      x1={fromX}
                      y1={fromY}
                      x2={midX}
                      y2={fromY}
                      stroke="#3B82F6"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                    
                    {/* Vertical line */}
                    <line
                      x1={midX}
                      y1={fromY}
                      x2={midX}
                      y2={toY}
                      stroke="#3B82F6"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                    
                    {/* Final horizontal line to target */}
                    <line
                      x1={midX}
                      y1={toY}
                      x2={toX}
                      y2={toY}
                      stroke="#3B82F6"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      markerEnd="url(#fk-arrowhead)"
                    />
                    
                    {/* FK Label positioned on the middle segment */}
                    <text
                      x={midX + 5}
                      y={(fromY + toY) / 2 - 5}
                      fontSize="10"
                      textAnchor="start"
                      fill="#3B82F6"
                      className="font-mono font-bold"
                    >
                      FK
                    </text>
                  </g>
                )
              })}
              
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7" 
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#374151"
                  />
                </marker>
                <marker
                  id="fk-arrowhead"
                  markerWidth="10"
                  markerHeight="7" 
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#3B82F6"
                  />
                </marker>
              </defs>
            </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Entity Modal */}
      {showEntityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Entity</h3>
              <button
                onClick={() => setShowEntityModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              const entity = dataModelingData.entities.find(e => e.id === showEntityModal)
              if (!entity) return null

              return (
                <div className="space-y-4">
                  {/* Entity Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entity Name
                    </label>
                    <input
                      type="text"
                      value={entity.name}
                      onChange={(e) => updateEntity(entity.id, { name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Attributes */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Attributes</label>
                      <button
                        onClick={() => addAttribute(entity.id)}
                        className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Attribute</span>
                      </button>
                    </div>

                    <div className="space-y-2">
                      {((entity as any).attributes || []).map((attr: any) => (
                        <div key={attr.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded">
                          <input
                            type="text"
                            value={attr.name}
                            onChange={(e) => updateAttribute(entity.id, attr.id, { name: e.target.value })}
                            className="col-span-3 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Name"
                          />
                          <input
                            type="text"
                            value={attr.type}
                            onChange={(e) => updateAttribute(entity.id, attr.id, { type: e.target.value })}
                            className="col-span-3 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Type"
                          />
                          <label className="col-span-2 flex items-center space-x-1 text-xs">
                            <input
                              type="checkbox"
                              checked={attr.isPrimary}
                              onChange={(e) => updateAttribute(entity.id, attr.id, { isPrimary: e.target.checked })}
                            />
                            <span>PK</span>
                          </label>
                          <label className="col-span-2 flex items-center space-x-1 text-xs">
                            <input
                              type="checkbox"
                              checked={attr.isForeign}
                              onChange={() => handleForeignKeyClick(entity.id, attr.id)}
                            />
                            <span>FK</span>
                          </label>
                          <label className="col-span-1 flex items-center space-x-1 text-xs">
                            <input
                              type="checkbox"
                              checked={attr.isRequired}
                              onChange={(e) => updateAttribute(entity.id, attr.id, { isRequired: e.target.checked })}
                            />
                            <span>Req</span>
                          </label>
                          <button
                            onClick={() => deleteAttribute(entity.id, attr.id)}
                            className="col-span-1 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowEntityModal(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Foreign Key Selection Modal */}
      {showFkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Select Foreign Key Reference</h3>
              <button
                onClick={() => setShowFkModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              const currentEntity = dataModelingData.entities.find(e => e.id === showFkModal.entityId)
              const currentAttribute = (currentEntity as any)?.attributes.find((a: any) => a.id === showFkModal.attributeId)
              
              return (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      Creating foreign key for <strong>{currentAttribute?.name}</strong> in <strong>{currentEntity?.name}</strong>
                    </p>
                    
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Table
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        const targetEntityId = e.target.value
                        if (targetEntityId) {
                          const targetEntity = dataModelingData.entities.find(ent => ent.id === targetEntityId)
                          const primaryKeyAttr = (targetEntity as any)?.attributes.find((attr: any) => attr.isPrimary)
                          
                          if (primaryKeyAttr) {
                            createForeignKeyRelationship(
                              showFkModal.entityId,
                              showFkModal.attributeId,
                              targetEntityId,
                              primaryKeyAttr.id
                            )
                            setShowFkModal(null)
                          } else {
                            alert('Target table must have a primary key attribute')
                          }
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="">Select a table...</option>
                      {dataModelingData.entities
                        .filter(entity => entity.id !== showFkModal.entityId) // Exclude current table
                        .filter(entity => ((entity as any).attributes || []).some((attr: any) => attr.isPrimary)) // Only tables with PK
                        .map((entity) => (
                          <option key={entity.id} value={entity.id}>
                            {entity.name}
                          </option>
                        ))
                      }
                    </select>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Note: Foreign key will reference the primary key of the selected table
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowFkModal(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
